import { useState, useEffect, useRef } from 'react'
import { IntlShape } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Modal, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { KTSVG } from '../../../../../../../_dashboard/helpers'
import { getAll, createOne, patchOne, deleteOne } from '../../../../../../services/common_requests'
import SeasonalPricingForm from './SeasonalPricingForm'
import { useConfirm } from '../../../../../../components/ConfirmProvider'

// Import Metronic menu
declare const KTMenu: any;

export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface SeasonalPrice {
  _id?: string
  tour: string
  priceHT: number
  priceTTC: number
  startDate: string
  endDate: string
  priority: number
  pricingMode: 'PER_PERSON' | 'PER_GROUP'
  personTypePrices: {
    personTypeId: string
    price: number
    priceHT: number
  }[]
  timeSlots: TimeSlot[]
  createdAt?: string
  updatedAt?: string
  __v?: number
}

interface SeasonalPricingProps {
  tourId: string
  intl: IntlShape
}

const SeasonalPricing: React.FC<SeasonalPricingProps> = ({
  tourId,
  intl,
}) => {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [currentItem, setCurrentItem] = useState<SeasonalPrice | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showDropdownId, setShowDropdownId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const confirm = useConfirm()

  // Fetch seasonal prices
  const { data: seasonalPrices, isLoading } = useQuery(
    ['tourSeasonalPricing', tourId],
    () => getAll(null, `/tour-pricing/seasonal-prices/${tourId}`),
    {
      enabled: !!tourId,
    }
  )

  // Create mutation
  const createMutation = useMutation(
    (data: Omit<SeasonalPrice, '_id' | 'createdAt' | 'updatedAt' | '__v'>) =>
      createOne(data, '/tour-pricing/seasonal-prices'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourSeasonalPricing', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.CREATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.CREATE_ERROR' }))
        setIsSaving(false)
      },
    }
  )

  // Update mutation - fixing the API endpoint to match the backend
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<SeasonalPrice> }) =>
      patchOne(data, `/tour-pricing/seasonal-prices/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourSeasonalPricing', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.UPDATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.UPDATE_ERROR' }))
        setIsSaving(false)
      },
    }
  )

  // Delete mutation
  const deleteMutation = useMutation(
    (id: string) => deleteOne(id, `/tour-pricing/seasonal-prices`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourSeasonalPricing', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.DELETE_SUCCESS' }))
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.DELETE_ERROR' }))
      },
    }
  )

  // Utility: Format a seasonal price for the form
  function formatSeasonalPriceForForm(item: SeasonalPrice, tourId: string): SeasonalPrice {
    return {
      ...item,
      tour: tourId,
      startDate: item.startDate ? new Date(item.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      endDate: item.endDate ? new Date(item.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      personTypePrices: item.pricingMode === 'PER_PERSON'
        ? (item.personTypePrices || []).map((ptp: any) => ({
            personTypeId: ptp.personType?._id || ptp.personTypeId || '',
            price: ptp.price,
            priceHT: ptp.priceHT,
          }))
        : [],
      timeSlots: item.timeSlots || [],
      priceHT: item.priceHT || 0,
      priceTTC: item.priceTTC || 0,
      priority: item.priority || 0,
      pricingMode: item.pricingMode || 'PER_GROUP',
    }
  }

  // Utility: Clean payload for API
  function cleanSeasonalPricePayload(values: SeasonalPrice, tourId: string) {
    const { __v, _id, createdAt, updatedAt, ...cleanedValues } = values
    const priceTTC = Number(values.priceTTC)
    const priceHT = Number(values.priceHT)
    const priority = Number(values.priority)
    const personTypePrices = values.pricingMode === 'PER_PERSON'
      ? values.personTypePrices.map(price => ({
      ...price,
      price: Number(price.price),
      priceHT: Number(price.priceHT),
    }))
      : []
    return {
      ...cleanedValues,
      tour: tourId,
      priceTTC,
      priceHT,
      priority,
      personTypePrices,
    }
    }

  // Handle form submission
  const handleSubmit = (values: SeasonalPrice) => {
    setIsSaving(true)
    const payload = cleanSeasonalPricePayload(values, tourId)
    if (currentItem?._id) {
      updateMutation.mutate({
        id: currentItem._id,
        data: payload,
      })
    } else {
      createMutation.mutate(payload as Omit<SeasonalPrice, '_id' | 'createdAt' | 'updatedAt' | '__v'>)
    }
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    const ok = await confirm({ message: intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.DELETE_CONFIRM' }) })
    if (!ok) return
    // Convert string id to number for the deleteOne function
    deleteMutation.mutate(id)
    setShowDropdownId(null)
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  // Handle opening the modal for adding a new item
  const handleAddNew = () => {
    setCurrentItem(formatSeasonalPriceForForm({
      tour: tourId,
      priceTTC: 0,
      priceHT: 0,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      priority: 0,
      pricingMode: 'PER_GROUP',
      personTypePrices: [],
      timeSlots: []
    }, tourId))
    setModalOpen(true)
  }

  // Handle opening the modal for editing an existing item
  const handleEdit = (item: SeasonalPrice) => {
    setCurrentItem(formatSeasonalPriceForForm(item, tourId))
    setModalOpen(true)
    setShowDropdownId(null)
  }

  // Close modal and reset state
  const closeModal = () => {
    setModalOpen(false)
    setCurrentItem(null)
  }

  // Toggle dropdown visibility
  const toggleDropdown = (id: string) => {
    setShowDropdownId(showDropdownId === id ? null : id)
  }

  // Initialize Metronic menu
  useEffect(() => {
    // Initialize all dropdown menus
    const menuElements = document.querySelectorAll('[data-kt-menu="true"]')
    menuElements.forEach((menu) => {
      if (typeof KTMenu !== 'undefined') {
        const menuInstance = new KTMenu(menu)
        menuInstance.on('kt.menu.dropdown.show', function() {
          // Handle menu show
        })
      }
    })
  }, [seasonalPrices]) // Re-initialize when data changes

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdownId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Render the list view
  const renderListView = () => {
    const items = seasonalPrices?.data || []
    
    // Sort by date range
    const sortedItems = [...items].sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    )

    return (
      <>
        <div className='d-flex justify-content-end mb-6'>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleAddNew}
          >
            <i className='ki-duotone ki-plus fs-2'></i>
            {intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.ADD' })}
          </button>
        </div>

        {sortedItems.length === 0 ? (
          <div className='text-center py-10'>
            <h3 className='fs-4 fw-bold mb-5'>
              {intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.NO_PRICES' })}
            </h3>
            <p className='fs-6 text-gray-700'>
              {intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.ADD_DESCRIPTION' })}
            </p>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5'>
              <thead>
                <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                  <th>{intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.DATE_RANGE' })}</th>
                  <th>{intl.formatMessage({ id: 'COLUMNS.PRICE' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.PRICING.MODE' })}</th>
                  <th className='text-end'>{intl.formatMessage({ id: 'COMMON.ACTIONS' })}</th>
                </tr>
              </thead>
              <tbody className='fw-semibold text-gray-600'>
                {sortedItems.map((item) => (
                  <tr key={item._id}>
                    <td>
                      {formatDate(item.startDate)} - {formatDate(item.endDate)}
                    </td>
                    <td>{item.priceTTC} €</td>
                    <td>
                      {item.pricingMode === 'PER_PERSON'
                        ? intl.formatMessage({ id: 'TOUR.PRICING.PER_PERSON' })
                        : intl.formatMessage({ id: 'TOUR.PRICING.PER_GROUP' })}
                    </td>
                    <td className='text-end'>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-primary btn-sm me-1'
                          onClick={() => handleEdit(item)}
                        >
                          <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-danger btn-sm'
                          onClick={() => handleDelete(item._id!)}
                        >
                          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-h-300px'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card card-flush'>
      <div className='card-body'>
        {renderListView()}

        {/* Modal for adding/editing seasonal pricing */}
        <Modal
          show={modalOpen}
          onHide={closeModal}
          size='lg'
          centered
          backdrop='static'
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {currentItem?._id
                ? intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.EDIT' })
                : intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.ADD' })}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentItem && (
              <SeasonalPricingForm
                initialValues={currentItem}
                onSubmit={handleSubmit}
                onCancel={closeModal}
                intl={intl}
                tourId={tourId}
                isSubmitting={isSaving}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  )
}

export default SeasonalPricing 